import { Component, OnInit, HostListener } from '@angular/core';
import { CorporateListService } from '../service/corporate-list.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';

export interface Professional {
  uid: string;
  category: string;
  name: string;
  contact_person: string | null;
  email: string;
  phone: string;
  country: string;
  users_allowed: number;
  account_activation_date: string | null;
  sub_start_date: string | null;
  sub_end_date: string | null;
  last_login: string | null;
  plan: string;
  status: string;
}

@Component({
  selector: 'app-corporate-list',
  templateUrl: './corporate-list.component.html',
  styleUrls: ['./corporate-list.component.css']
})
export class CorporateListComponent implements OnInit {

  // ─── Data ─────────────────────────────────────────────────────────────────
  allProfessionals: Professional[] = [];
  filteredData: Professional[] = [];
  dataSource: Professional[] = [];

  // ─── State ────────────────────────────────────────────────────────────────
  isLoading: boolean = false;

  // ─── Search / Filter ──────────────────────────────────────────────────────
  searchQuery: string = '';
  statusFilter: string = '';
  isStatusDropdownOpen: boolean = false;

  // ─── Sort ─────────────────────────────────────────────────────────────────
  sortColumn: string = '';
  sortDirection: 'asc' | 'desc' | '' = '';

  // ─── Pagination ───────────────────────────────────────────────────────────
  pageIndex: number = 0;
  pageSize: number = 10;

  constructor(
    private corporateListService: CorporateListService,
    private spinner: NgxSpinnerService,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    this.loadProfessionals();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.custom-dropdown')) {
      this.isStatusDropdownOpen = false;
    }
  }

  // ═══════════════════════════════════════════════════════════
  //  API
  // ═══════════════════════════════════════════════════════════

  loadProfessionals(): void {
    this.isLoading = true;
    this.spinner.show();

    this.corporateListService.getCorporateProfessionals().subscribe(
      (response: any) => {
        if (response && Array.isArray(response.data)) {
          this.allProfessionals = response.data;
        } else {
          this.allProfessionals = [];
        }
        this.applyFilter();
        this.isLoading = false;
        this.spinner.hide();
      },
      (error: any) => {
        console.error('Error loading Corporate professionals:', error);
        this.toastr.error('Failed to load Corporate professionals', 'Error');
        this.allProfessionals = [];
        this.dataSource = [];
        this.isLoading = false;
        this.spinner.hide();
      }
    );
  }

  // ═══════════════════════════════════════════════════════════
  //  FILTER & SORT
  // ═══════════════════════════════════════════════════════════

  applyFilter(): void {
    const q      = this.searchQuery.trim().toLowerCase();
    const status = this.statusFilter.toLowerCase();

    let filtered = [...this.allProfessionals];

    if (q) {
      filtered = filtered.filter(p =>
        (p.name           || '').toLowerCase().includes(q) ||
        (p.category       || '').toLowerCase().includes(q) ||
        (p.contact_person || '').toLowerCase().includes(q) ||
        (p.email          || '').toLowerCase().includes(q) ||
        (p.phone          || '').toLowerCase().includes(q) ||
        (p.status         || '').toLowerCase().includes(q)
      );
    }

    if (status) {
      filtered = filtered.filter(p => (p.status || '').toLowerCase() === status);
    }

    this.filteredData = filtered;
    this.pageIndex = 0;

    if (this.sortColumn && this.sortDirection) {
      this.applySort();
    } else {
      this.dataSource = [...this.filteredData];
    }
  }

  applySort(): void {
    if (!this.sortColumn || this.sortDirection === '') {
      this.dataSource = [...this.filteredData];
      return;
    }

    const data = [...this.filteredData];
    const dir  = this.sortDirection === 'asc' ? 1 : -1;
    const col  = this.sortColumn as keyof Professional;

    data.sort((a, b) => {
      let valA = a[col];
      let valB = b[col];

      if (col === 'account_activation_date' || col === 'sub_start_date' || col === 'sub_end_date' || col === 'last_login') {
        const tA = valA ? new Date(valA as string).getTime() : 0;
        const tB = valB ? new Date(valB as string).getTime() : 0;
        return (tA - tB) * dir;
      }

      valA = (valA || '').toString().toLowerCase();
      valB = (valB || '').toString().toLowerCase();
      if (valA < valB) return -1 * dir;
      if (valA > valB) return  1 * dir;
      return 0;
    });

    this.dataSource = data;
  }

  sortBy(column: string): void {
    if (this.sortColumn === column) {
      if (this.sortDirection === 'asc')       { this.sortDirection = 'desc'; }
      else if (this.sortDirection === 'desc') { this.sortDirection = ''; this.sortColumn = ''; }
      else                                    { this.sortDirection = 'asc'; }
    } else {
      this.sortColumn    = column;
      this.sortDirection = 'asc';
    }
    this.applySort();
  }

  getSortIcon(column: string): string {
    if (this.sortColumn !== column || this.sortDirection === '') {
      return 'bi-arrow-down-up sort-icon-neutral';
    }
    return this.sortDirection === 'asc'
      ? 'bi-arrow-up sort-icon-active'
      : 'bi-arrow-down sort-icon-active';
  }

  // ═══════════════════════════════════════════════════════════
  //  SEARCH
  // ═══════════════════════════════════════════════════════════

  onSearch(): void { this.applyFilter(); }

  clearSearch(): void {
    this.searchQuery = '';
    this.applyFilter();
  }

  // ═══════════════════════════════════════════════════════════
  //  STATUS DROPDOWN
  // ═══════════════════════════════════════════════════════════

  toggleStatusDropdown(): void {
    this.isStatusDropdownOpen = !this.isStatusDropdownOpen;
  }

  selectStatus(status: string): void {
    this.statusFilter = status;
    this.isStatusDropdownOpen = false;
    this.applyFilter();
  }

  clearStatusFilter(): void {
    this.statusFilter = '';
    this.applyFilter();
  }

  // ═══════════════════════════════════════════════════════════
  //  PAGINATION
  // ═══════════════════════════════════════════════════════════

  getPage(): Professional[] {
    const start = this.pageIndex * this.pageSize;
    return this.dataSource.slice(start, start + this.pageSize);
  }

  getTotalPages(): number {
    return Math.max(1, Math.ceil(this.dataSource.length / this.pageSize));
  }

  getPageNumbers(): number[] {
    const total   = this.getTotalPages();
    const current = this.pageIndex + 1;
    const maxVis  = 5;
    let start = Math.max(1, current - Math.floor(maxVis / 2));
    let end   = Math.min(total, start + maxVis - 1);
    if (end - start < maxVis - 1) start = Math.max(1, end - maxVis + 1);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  }

  getEndIndex(): number {
    return Math.min((this.pageIndex + 1) * this.pageSize, this.dataSource.length);
  }

  getPaginationRange(): string {
    if (this.dataSource.length === 0) return '0-0 of 0 items';
    return `${this.pageIndex * this.pageSize + 1}-${this.getEndIndex()} of ${this.dataSource.length} items`;
  }

  goToFirstPage(): void { if (this.pageIndex !== 0) this.pageIndex = 0; }
  goToPrevPage(): void  { if (this.pageIndex > 0) this.pageIndex--; }
  goToNextPage(): void  { if (this.pageIndex < this.getTotalPages() - 1) this.pageIndex++; }
  goToLastPage(): void  { this.pageIndex = this.getTotalPages() - 1; }
  goToPage(index: number): void {
    if (index >= 0 && index < this.getTotalPages()) this.pageIndex = index;
  }

  // ═══════════════════════════════════════════════════════════
  //  HELPERS
  // ═══════════════════════════════════════════════════════════

  formatDate(date: string | null): string {
    if (!date) return '-';
    const d = new Date(date);
    if (isNaN(d.getTime())) return '-';
    return d.toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: '2-digit'
    });
  }

  formatDateTime(date: string | null): string {
    if (!date) return '-';
    const d = new Date(date);
    if (isNaN(d.getTime())) return '-';
    const datePart = d.toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: '2-digit'
    });
    const timePart = d.toLocaleTimeString('en-US', {
      hour: '2-digit', minute: '2-digit', hour12: true
    });
    return `${datePart} | ${timePart}`;
  }

  getStatusClass(status: string): string {
    const map: { [key: string]: string } = {
      'active':   'status-active',
      'inactive': 'status-inactive',
      'pending':  'status-pending',
      'invited':  'status-invited'
    };
    return map[(status || '').toLowerCase()] || 'status-default';
  }

  getInitials(name: string): string {
    if (!name) return '?';
    const parts = name.trim().split(' ');
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
  }
}